import React, {useCallback, useEffect, useRef, useState} from 'react';
import SoftBox from '../../../components/SoftBox';
import SoftTypography from '../../../components/SoftTypography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import {getBranchDetailById} from "../../../api/branch/branchApiClient";
import {useParams} from "react-router-dom";
import SoftButton from "../../../components/SoftButton";
import AddEditCCTVConfigDialog from "./AddEditCCTVConfigDialog";
import {addCCTVConfig, deleteCCTVConfig, updateCCTVConfig} from "../../../api/branch/cctvConfigApiClient";
import AddEditRoomDialog from "./AddEditRoomDialog";
import {addRoom, deleteRoom, updateRoom} from "../../../api/branch/roomApiClient";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import {Tab, Tabs, Tooltip} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import DeleteConfirmDialog from "../../../components/Dialogs/DeleteConfirmDialog";
import AddEditCameraDialog from "./AddEditCameraDialog";
import {
    addCamera,
    deleteCamera,
    getAllCameraByBranchId,
    removeCameraRoomMapping,
    updateCamera,
    updateCameraRoomMapping
} from "../../../api/branch/cameraApiClient";
import AddEditRoomCameraMappingDialog from "./AddEditRoomCameraMappingDialog";
import RestrictedZoneDialog from "./RestrictedZoneDialog";
import {getRestrictedZoneCountsByBranch} from "../../../api/branch/restrictedZoneApiClient";
import CameraRoomMappingTable from "./CameraRoomMappingTable";
import LogoGenerator from "../../../utils/logoGenerator";
import {handleApiError, showCreateSuccess, showUpdateSuccess, showDeleteSuccess} from "../../../utils/notificationUtils";
import {getCompanyById} from "../../../api/company/companyApiClient";
import ViewCompanyDialog from "../../company/components/ViewCompanyDialog";
import SoftAlert from "../../../components/Alert";

const BranchDetails = () => {
    const theme = useTheme();
    const {branchId} = useParams();

    const [activeTab, setActiveTab] = useState(0);
    const [branchDetail, setBranchDetail] = useState({});
    const [branch, setBranch] = useState({});
    const [cameraDetails, setCameraDetails] = useState([]);
    const [companyToView, setCompanyToView] = useState(null);
    const [viewCompanyDialogOpen, setViewCompanyDialogOpen] = useState(false);
    const isMountedRef = useRef(true);

    const [addEditCCTVDialogOpen, setAddEditCCTVDialogOpen] = useState(false);
    const [addEditRoomDialogOpen, setAddEditRoomDialogOpen] = useState(false);
    const [addEditCameraDialogOpen, setAddEditCameraDialogOpen] = useState(false);
    const [addEditCameraRoomMappingDialogOpen, setAddEditCameraRoomMappingDialogOpen] = useState(false);
    const [restrictedZoneDialogOpen, setRestrictedZoneDialogOpen] = useState(false);
    // {cameraId: zoneCount} for the badge — one query per branch rather than
    // one per camera.
    const [restrictedZoneCounts, setRestrictedZoneCounts] = useState({});

    const [deleteCCTVDialogOpen, setDeleteCCTVDialogOpen] = useState(false);
    const [deleteRoomDialogOpen, setDeleteRoomDialogOpen] = useState(false);
    const [deleteCameraDialogOpen, setDeleteCameraDialogOpen] = useState(false);
    const [deleteCameraMappingDialogOpen, setDeleteCameraMappingDialogOpen] = useState(false);

    const [selectedCCTVConfig, setSelectedCCTVConfig] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [selectedCamera, setSelectedCamera] = useState(null);
    const [selectedMapping, setSelectedMapping] = useState(null);

    const [editCCTVMode, setEditCCTVMode] = useState(false);
    const [editRoomMode, setEditRoomMode] = useState(false);
    const [editCameraMode, setEditCameraMode] = useState(false);
    const [editCameraRoomMode, setEditCameraRoomMode] = useState(false);

    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const loadBranchDetails = useCallback(() => {
        getBranchDetailById(branchId).then(res => {
            if (isMountedRef.current) {
                setBranchDetail(res.data);
                setBranch(res.data.branch);
            }
        });
        getAllCameraByBranchId(branchId).then(res => {
            if (isMountedRef.current) {
                let filtered = res.data.filter(r => r.room !== null);
                setCameraDetails(filtered);
            }
        })
        getRestrictedZoneCountsByBranch(branchId).then(counts => {
            if (isMountedRef.current) {
                setRestrictedZoneCounts(counts || {});
            }
        }).catch(() => {
            // Only drives a badge — a failure here must not blank the page.
            if (isMountedRef.current) setRestrictedZoneCounts({});
        });
    }, [branchId]);

    useEffect(() => {
        if (isMountedRef.current) {
            loadBranchDetails();
        }
    }, [loadBranchDetails]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleCompanyNameClick = async () => {
        try {
            // Check if company object exists, otherwise fetch using companyId
            if (branch.company) {
                setCompanyToView(branch.company);
                setViewCompanyDialogOpen(true);
            } else if (branch.companyId) {
                const response = await getCompanyById(branch.companyId);
                if (response && response.data && isMountedRef.current) {
                    setCompanyToView(response.data);
                    setViewCompanyDialogOpen(true);
                } else if (!response?.data) {
                    console.warn("Company data not found for ID:", branch.companyId);
                    handleApiError(new Error("Company details not found."), "Failed to load company details.");
                }
            } else {
                console.warn("No company or companyId found for branch:", branch.branchId);
                handleApiError(new Error("Company details not available."), "Company details not available.");
            }
        } catch (error) {
            console.error('Error loading company details:', error);
            handleApiError(error, 'Failed to load company details. Please try again.');
        }
    };

    const handleAddCamera = (cctv) => {
        setSelectedCCTVConfig(cctv);
        setSelectedCamera(null);
        setEditCameraMode(false);
        setAddEditCameraDialogOpen(true);
    };

    const handleAddCCTVConfig = () => {
        setSelectedCCTVConfig(null);
        setEditCCTVMode(false);
        setAddEditCCTVDialogOpen(true);
    };

    const handleAddCameraMapping = () => {
        setAddEditCameraRoomMappingDialogOpen(true);
        setSelectedCamera(null);
        setSelectedRoom(null);
        setSelectedCCTVConfig(null);
        setSelectedMapping(null);
    };

    const handleEditCCTVConfig = (cctvConfig) => {
        setSelectedCCTVConfig(cctvConfig);
        setEditCCTVMode(true);
        setAddEditCCTVDialogOpen(true);
    };

    const handleEditCameraMapping = (mapping) => {
        setSelectedMapping(mapping);
        setEditCameraRoomMode(true)
        setAddEditCameraRoomMappingDialogOpen(true);
    };

    const handleEditRoom = (room) => {
        setSelectedRoom(room);
        setEditRoomMode(true);
        setAddEditRoomDialogOpen(true);
    };

    const handleEditCamera = (camera, cctv) => {
        setSelectedCCTVConfig(cctv)
        setSelectedCamera(camera);
        setEditCameraMode(true);
        setAddEditCameraDialogOpen(true);
    };

    const handleEditRestrictedZones = (camera) => {
        setSelectedCamera(camera);
        setRestrictedZoneDialogOpen(true);
    }

    const handleDeleteCamera = (camera) => {
        setSelectedCamera(camera);
        setDeleteCameraDialogOpen(true);
    };

    const handleDeleteCCTVConfig = (cctvConfig) => {
        setSelectedCCTVConfig(cctvConfig);
        setDeleteCCTVDialogOpen(true);
    };

    const handleDeleteRoom = (room) => {
        setSelectedRoom(room);
        setDeleteRoomDialogOpen(true);
    };

    const handleDeleteCameraRoomMapping = (mapping) => {
        setSelectedMapping(mapping);
        setDeleteCameraMappingDialogOpen(true);
    };

    const onCloseDeleteDialog = () => {
        setSelectedCCTVConfig(null);
        setSelectedRoom(null);
        setSelectedCamera(null);
        setSelectedMapping(null);
        setDeleteCCTVDialogOpen(false);
        setDeleteRoomDialogOpen(false);
        setDeleteCameraDialogOpen(false);
        setDeleteCameraMappingDialogOpen(false);
        setShowAlert(false);
        setError(null);
    }

    const handleOnSubmitCameraRoom = async (formData) => {
      try {
        const response = await updateCameraRoomMapping(formData.cameraId, formData);
        console.log('Camera room mapping update response:', response);
        await loadBranchDetails();
        // Reload the mapping list to get updated status
        if (typeof window !== 'undefined' && window.location) {
          // Trigger a refresh of the branch details to show updated status
        }
      } catch (error) {
        console.error('Error saving camera Mapping:', error);
        setError(`Failed to save camera mapping: ${error.response?.data?.message || 'Please try again'}`);
        setShowAlert(true);
      }
      setAddEditCameraRoomMappingDialogOpen(false);
      setSelectedCamera(null);
      setSelectedRoom(null);
      setSelectedCCTVConfig(null);
    };

    const handleOnSubmitCameraConfig = async (formData) => {
        try {
            if (editCameraMode) {
                await updateCamera(selectedCamera?.cameraId, formData);
                showUpdateSuccess('Camera');
            } else {
                await addCamera(formData);
                showCreateSuccess('Camera');
            }
            await loadBranchDetails();
            setAddEditCameraDialogOpen(false);
            setSelectedCamera(null);
        } catch (error) {
            console.error('Error saving camera:', error);
            handleApiError(error, editCameraMode ? "Failed to update camera" : "Failed to create camera");
            // Don't close dialog on error
        }
    };

    const handleOnSubmitCCTVConfig = async (formData) => {
        try {
            if (editCCTVMode) {
                await updateCCTVConfig(selectedCCTVConfig?.configId, formData);
                showUpdateSuccess('CCTV Config');
            } else {
                await addCCTVConfig(formData);
                showCreateSuccess('CCTV Config');
            }
            await loadBranchDetails();
            setAddEditCCTVDialogOpen(false);
            setSelectedCCTVConfig(null);
        } catch (error) {
            console.error('Error saving cctvConfig:', error);
            // Show error notification with actual backend error message
            handleApiError(error, editCCTVMode ? "Failed to update CCTV config" : "Failed to create CCTV config");
            // Don't close dialog on error - let user fix and retry
        }
    };

    const handleOnSubmitRoomConfig = async (formData) => {
        try {
            if (editRoomMode) {
                await updateRoom(selectedRoom.roomId, formData);
                showUpdateSuccess('Room');
            } else {
                await addRoom(formData);
                showCreateSuccess('Room');
            }
            await loadBranchDetails();
            setAddEditRoomDialogOpen(false);
            setSelectedRoom(null);
            setEditRoomMode(false);
        } catch (error) {
            console.error('Error saving room :', error);
            handleApiError(error, editRoomMode ? "Failed to update room" : "Failed to create room");
            // Don't close dialog on error
        }
    };

    function handleOnDeleteCCTVConfig() {
      deleteCCTVConfig(selectedCCTVConfig?.configId).then(r => {
          onCloseDeleteDialog();
          loadBranchDetails();
        }
      ).catch(err => {
        // The API client already unwraps the server message into err.message.
        setError(err.message || 'Failed to delete CCTV config.');
        setShowAlert(true);
      });
    }

    function handleOnDeleteRoom() {
      deleteRoom(selectedRoom.roomId).then(r => {
          onCloseDeleteDialog();
          loadBranchDetails();
        }
      ).catch(err => {
        // Check if error message contains foreign key constraint information
        const errorMessage = err.response?.data?.message || err.message || 'Failed to delete Room';
        if (errorMessage && errorMessage.includes('foreign key constraint') || errorMessage.includes('camera')) {
          setError('Cannot delete room. This room has cameras assigned to it. Please remove camera assignments before deleting.');
        } else {
          setError(errorMessage);
        }
        setShowAlert(true);
      });
    }

    function handleOnDeleteCamera() {
      deleteCamera(selectedCamera.cameraId).then(r => {
          onCloseDeleteDialog();
          loadBranchDetails();
        }
      ).catch(err => {
        setError(err.response?.data?.message || 'Failed to delete Camera.');
        setShowAlert(true);
      });
    }

    function handleOnDeleteRoomCameraMapping() {
      removeCameraRoomMapping(selectedMapping?.camera?.cameraId).then(r => {
          onCloseDeleteDialog();
          loadBranchDetails();
        }
      ).catch(err => {
        setError(err.response?.data?.message || 'Failed to delete mapping.');
        setShowAlert(true);
      });
    }

    return (
        <>
            <SoftAlert color="error" mount={showAlert && !!error}>
                {error}
            </SoftAlert>
            <SoftBox sx={{
                background: `linear-gradient(135deg, ${theme.palette.grey[100]} 0%, ${theme.palette.grey[300]} 100%)`,
                minHeight: '100vh',
                p: {xs: 1, sm: 2}
            }}>
                <Grid container spacing={{xs: 2, sm: 3}}>
                    <Grid item xs={12} md={4} lg={3}>
                        <Card sx={{
                            boxShadow: 4,
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 6
                            }
                        }}>
                            <SoftBox sx={{
                                position: 'relative',
                                height: 200,
                                overflow: 'hidden'
                            }}>
                                <img
                                    src={LogoGenerator.generateLocationLogo(branch.location || branch.name)}
                                    alt={branch.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '12px'
                                    }}
                                />
                                <SoftBox sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                    p: 2
                                }}>
                                    <SoftTypography variant="h5" fontWeight="bold" color="white">
                                        {branch.name}
                                    </SoftTypography>
                                    <SoftTypography variant="body2" color="rgba(255,255,255,0.9)">
                                        {branch.location}
                                    </SoftTypography>
                                </SoftBox>
                            </SoftBox>

                            <SoftBox p={3}>
                                <SoftBox sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(103, 126, 234, 0.1)'
                                    }}>
                                        <Icon color="primary" sx={{fontSize: 20}}>business</Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary"
                                                            fontWeight="medium">
                                                Company
                                            </SoftTypography>
                                            {branch.companyName && branch.companyName !== "N/A" ? (
                                                <SoftTypography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                    color="primary"
                                                    sx={{
                                                        cursor: "pointer",
                                                        "&:hover": {textDecoration: "underline"}
                                                    }}
                                                    onClick={handleCompanyNameClick}
                                                >
                                                    {branch.companyName}
                                                </SoftTypography>
                                            ) : (
                                                <SoftTypography variant="body1" fontWeight="regular" color="text">
                                                    {branch.companyName || "N/A"}
                                                </SoftTypography>
                                            )}
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(103, 126, 234, 0.1)'
                                    }}>
                                        <Icon color="primary" sx={{fontSize: 20}}>location_on</Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
                                                Address
                                            </SoftTypography>
                                            <SoftTypography variant="body1" fontWeight="medium">
                                                {branch.address}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(118, 75, 162, 0.1)'
                                    }}>
                                        <Icon color="inherit" sx={{fontSize: 20}}>schedule</Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
                                                Operating Hours
                                            </SoftTypography>
                                            <SoftTypography variant="body1" fontWeight="medium">
                                                {branch.openTime} - {branch.closeTime}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)'
                                    }}>
                                        <Icon color="success" sx={{fontSize: 20}}>phone</Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
                                                Contact
                                            </SoftTypography>
                                            <SoftTypography variant="body1" fontWeight="medium">
                                                {branch.phoneNo}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: (branch.faceDetectionEnabled === true) ? 'rgba(33, 150, 243, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                                    }}>
                                        <Icon color={(branch.faceDetectionEnabled === true) ? "info" : "error"}
                                              sx={{fontSize: 20}}>
                                            {(branch.faceDetectionEnabled === true) ? 'face' : 'face_retouching_off'}
                                        </Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
                                                Face Detection
                                            </SoftTypography>
                                            <SoftTypography variant="body1" fontWeight="medium"
                                                            color={(branch.faceDetectionEnabled === true) ? "info.main" : "error.main"}>
                                                {(branch.faceDetectionEnabled === true) ? 'Enabled' : 'Disabled'}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    <SoftBox sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: (branch.isActive === true) ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)'
                                    }}>
                                        <Icon color={(branch.isActive === true) ? "success" : "disabled"}
                                              sx={{fontSize: 20}}>
                                            {(branch.isActive === true) ? 'check_circle' : 'disabled'}
                                        </Icon>
                                        <SoftBox>
                                            <SoftTypography variant="body2" color="text.secondary" fontWeight="medium">
                                                Branch Status
                                            </SoftTypography>
                                            <SoftTypography variant="body1" fontWeight="medium"
                                                            color={(branch.isActive === true) ? "success.main" : "disabled.main"}>
                                                {(branch.isActive === true) ? 'Active' : 'Inactive'}
                                            </SoftTypography>
                                        </SoftBox>
                                    </SoftBox>

                                    {branch.weburl && (
                                        <SoftBox sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: 2,
                                            backgroundColor: 'rgba(255, 152, 0, 0.1)'
                                        }}>
                                            <Icon color="warning" sx={{fontSize: 20}}>language</Icon>
                                            <SoftBox>
                                                <SoftTypography variant="body2" color="text.secondary"
                                                                fontWeight="medium">
                                                    Website
                                                </SoftTypography>
                                                <SoftTypography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                    component="a"
                                                    href={branch.weburl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        color: 'primary.main',
                                                        textDecoration: 'none',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    Visit Website
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    )}
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Grid>

                    {/* Main Content Area */}
                    <Grid item xs={12} md={8} lg={9}>
                        <Card sx={{
                            boxShadow: 4,
                            borderRadius: 3,
                            background: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(10px)',
                            overflow: 'hidden'
                        }}>
                            {/* Enhanced Tabs */}
                            <SoftBox sx={{
                                background: `linear-gradient(135deg, ${theme.palette.light.main} 0%, ${theme.palette.gradients.primary.state} 100%)`,
                                p: {xs: 1, sm: 2}
                            }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    aria-label="Branch configuration tabs"
                                    textColor="inherit"
                                    indicatorColor="secondary"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        '& .MuiTab-root': {
                                            color: 'rgba(255,255,255,0.8)',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            fontSize: {xs: '0.875rem', sm: '1rem'},
                                            minHeight: 48,
                                            '&.Mui-selected': {
                                                color: 'white',
                                                fontWeight: 'bold'
                                            }
                                        },
                                        '& .MuiTabs-indicator': {
                                            backgroundColor: 'white',
                                            height: 3
                                        }
                                    }}
                                >
                                    <Tab
                                        label={
                                            <SoftBox display="flex" alignItems="center" gap={1}>
                                                <Icon sx={{fontSize: 20}}>videocam</Icon>
                                                <span>CCTV Configs</span>
                                            </SoftBox>
                                        }
                                    />
                                    <Tab
                                        label={
                                            <SoftBox display="flex" alignItems="center" gap={1}>
                                                <Icon sx={{fontSize: 20}}>meeting_room</Icon>
                                                <span>Rooms</span>
                                            </SoftBox>
                                        }
                                    />
                                    <Tab
                                        label={
                                            <SoftBox display="flex" alignItems="center" gap={1}>
                                                <Icon sx={{fontSize: 20}}>settings_input_component</Icon>
                                                <span>Camera Mapping</span>
                                            </SoftBox>
                                        }
                                    />
                                </Tabs>
                            </SoftBox>

                            {/* Tab Content */}
                            <SoftBox sx={{p: {xs: 2, sm: 3}}}>
                                {activeTab === 1 && (
                                    <SoftBox>
                                        <SoftBox display="flex" alignItems="center" justifyContent="space-between"
                                                 mb={3}>
                                            <SoftTypography variant="h5" fontWeight="bold" color="primary">
                                                Rooms ({branchDetail?.rooms?.length || 0})
                                            </SoftTypography>
                                            <SoftButton
                                                onClick={() => setAddEditRoomDialogOpen(true)}
                                                variant="contained"
                                                color="primary"
                                                startIcon={<Icon>add</Icon>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Add Room
                                            </SoftButton>
                                        </SoftBox>

                                        {branchDetail?.rooms?.length > 0 ? (
                                            <Grid container spacing={3}>
                                                {branchDetail?.rooms?.map((room, idx) => (
                                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                                        <Card sx={{
                                                            p: 3,
                                                            boxShadow: 2,
                                                            borderRadius: 3,
                                                            background: `linear-gradient(135deg, ${theme.palette.white.main} 0%, ${theme.palette.grey[100]} 100%)`,
                                                            border: `1px solid ${theme.palette.grey[300]}`,
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                transform: 'translateY(-4px)',
                                                                boxShadow: 4
                                                            }
                                                        }}>
                                                            <SoftBox display="flex" alignItems="center"
                                                                     justifyContent="space-between" mb={2}>
                                                                <SoftBox>
                                                                    <SoftTypography variant="h6" fontWeight="bold"
                                                                                    color="primary">
                                                                        {room.name}
                                                                    </SoftTypography>
                                                                    <SoftTypography variant="body2"
                                                                                    color="text.secondary">
                                                                        Room #{room.number}
                                                                    </SoftTypography>
                                                                </SoftBox>
                                                                <SoftBox display="flex" gap={0.5}>
                                                                    <IconButton
                                                                        onClick={() => handleEditRoom(room)}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: 'primary.main',
                                                                            color: 'white',
                                                                            '&:hover': {
                                                                                backgroundColor: 'primary.dark'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">edit</Icon>
                                                                    </IconButton>
                                                                    <IconButton
                                                                        onClick={() => handleDeleteRoom(room)}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: 'error.main',
                                                                            color: 'white',
                                                                            '&:hover': {
                                                                                backgroundColor: 'error.dark'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">delete</Icon>
                                                                    </IconButton>
                                                                </SoftBox>
                                                            </SoftBox>

                                                            <SoftBox sx={{
                                                                p: 2.5,
                                                                borderRadius: 2,
                                                                backgroundColor: 'rgba(103, 126, 234, 0.05)',
                                                                border: '1px solid rgba(103, 126, 234, 0.1)'
                                                            }}>
                                                                <SoftBox sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 1.5
                                                                }}>
                                                                    <SoftBox display="flex"
                                                                             justifyContent="space-between"
                                                                             alignItems="center">
                                                                        <SoftTypography variant="body2"
                                                                                        color="text.secondary">Capacity:</SoftTypography>
                                                                        <SoftTypography variant="body2"
                                                                                        fontWeight="medium">{room.capacity} people</SoftTypography>
                                                                    </SoftBox>
                                                                    <SoftBox display="flex"
                                                                             justifyContent="space-between"
                                                                             alignItems="center">
                                                                        <SoftTypography variant="body2"
                                                                                        color="text.secondary">Age
                                                                            Group:</SoftTypography>
                                                                        <SoftTypography variant="body2"
                                                                                        fontWeight="medium">{room.ageGroup}</SoftTypography>
                                                                    </SoftBox>
                                                                    {room.description && (
                                                                        <SoftBox display="flex"
                                                                                 justifyContent="space-between"
                                                                                 alignItems="flex-start">
                                                                            <SoftTypography variant="body2"
                                                                                            color="text.secondary">Description:</SoftTypography>
                                                                            <SoftTypography variant="body2"
                                                                                            fontWeight="medium" sx={{
                                                                                maxWidth: '60%',
                                                                                textAlign: 'right'
                                                                            }}>
                                                                                {room.description}
                                                                            </SoftTypography>
                                                                        </SoftBox>
                                                                    )}
                                                                </SoftBox>
                                                            </SoftBox>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <SoftBox sx={{
                                                p: 6,
                                                textAlign: 'center',
                                                backgroundColor: 'rgba(0,0,0,0.05)',
                                                borderRadius: 3
                                            }}>
                                                <Icon
                                                    sx={{
                                                        fontSize: 64,
                                                        color: 'text.secondary',
                                                        mb: 3
                                                    }}>meeting_room</Icon>
                                                <SoftTypography variant="h5" color="text.secondary" mb={2}>
                                                    No Rooms
                                                </SoftTypography>
                                                <SoftTypography variant="body1" color="text.secondary" mb={3}>
                                                    No rooms have been added to this branch yet.
                                                </SoftTypography>
                                                <SoftButton
                                                    onClick={() => setAddEditRoomDialogOpen(true)}
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<Icon>add</Icon>}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Add First Room
                                                </SoftButton>
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                )}
                                {activeTab === 0 && (
                                    <Card sx={{boxShadow: 2}}>
                                        <SoftBox sx={{m: {xs: 0.5, sm: 1}}}>
                                            <SoftBox p={2} px={2} display="flex" alignItems="center"
                                                     justifyContent="space-between"
                                                     width="100%"
                                                     sx={{
                                                         flexDirection: {xs: 'column', sm: 'row'},
                                                         gap: {xs: 1, sm: 0}
                                                     }}>
                                                <SoftBox>
                                                    <SoftTypography variant="h6" fontWeight="medium">
                                                        CCTV Configuration
                                                    </SoftTypography>
                                                </SoftBox>
                                                <SoftButton onClick={handleAddCCTVConfig} color="primary">
                                                    Add Config
                                                </SoftButton>
                                            </SoftBox>
                                            {branchDetail?.cctv?.map((cctv, idx) => (
                                                <SoftBox key={idx} mb={3}>
                                                    <Card sx={{
                                                        boxShadow: 3,
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        background: `linear-gradient(135deg, ${theme.palette.white.main} 0%, ${theme.palette.grey[100]} 100%)`,
                                                        border: `1px solid ${theme.palette.grey[300]}`
                                                    }}>
                                                        {/* Header Section */}
                                                        <SoftBox sx={{
                                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.focus} 100%)`,
                                                            p: {xs: 2, sm: 3},
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            flexDirection: {xs: 'column', sm: 'row'},
                                                            gap: {xs: 2, sm: 0}
                                                        }}>
                                                            <SoftBox>
                                                                <SoftTypography variant="h5" fontWeight="bold"
                                                                            color="white">
                                                                    {cctv.locationDescription}
                                                                </SoftTypography>
                                                                <SoftTypography variant="body2"
                                                                            color="rgba(255,255,255,0.8)">
                                                                    CCTV Configuration
                                                                </SoftTypography>
                                                            </SoftBox>
                                                            <SoftBox display="flex" gap={1}>
                                                                <IconButton
                                                                    onClick={() => handleEditCCTVConfig(cctv)}
                                                                    sx={{
                                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                                        color: 'white',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(255,255,255,0.3)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Icon>edit</Icon>
                                                                </IconButton>
                                                                <IconButton
                                                                    onClick={() => handleDeleteCCTVConfig(cctv)}
                                                                    sx={{
                                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                                        color: 'white',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(255,255,255,0.3)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Icon>delete</Icon>
                                                                </IconButton>
                                                            </SoftBox>
                                                        </SoftBox>

                                                        <SoftBox sx={{p: {xs: 2, sm: 3}}}>
                                                            <Grid container spacing={3}>
                                                                <Grid item xs={12} md={4}>
                                                                    <Card sx={{
                                                                        boxShadow: 2,
                                                                        borderRadius: 2,
                                                                        background: 'rgba(255,255,255,0.95)',
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}>
                                                                        <SoftBox p={3}>
                                                                            <SoftTypography variant="h6"
                                                                                            fontWeight="bold"
                                                                                            mb={2} color="primary">
                                                                                Configuration Details
                                                                            </SoftTypography>
                                                                            <SoftBox sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: 1.5
                                                                            }}>
                                                                                <SoftBox display="flex"
                                                                                         justifyContent="space-between"
                                                                                         alignItems="center">
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">IP
                                                                                        :</SoftTypography>
                                                                                    <SoftTypography variant="body2"
                                                                                                    fontWeight="medium">{cctv.ipAddress}</SoftTypography>
                                                                                </SoftBox>
                                                                                <SoftBox display="flex"
                                                                                         justifyContent="space-between"
                                                                                         alignItems="center">
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">Host
                                                                                        :</SoftTypography>
                                                                                    <SoftTypography variant="body2"
                                                                                                    fontWeight="medium">{cctv.host}</SoftTypography>
                                                                                </SoftBox>
                                                                                <SoftBox display="flex"
                                                                                         justifyContent="space-between"
                                                                                         alignItems="center">
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">Port
                                                                                        :</SoftTypography>
                                                                                    <SoftTypography variant="body2"
                                                                                                    fontWeight="medium">{cctv.port}</SoftTypography>
                                                                                </SoftBox>
                                                                                <SoftBox display="flex"
                                                                                         justifyContent="space-between"
                                                                                         alignItems="center">
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">DDNS
                                                                                        :</SoftTypography>
                                                                                    <SoftTypography variant="body2"
                                                                                                    fontWeight="medium">{cctv.ddns}</SoftTypography>
                                                                                </SoftBox>
                                                                                <SoftBox display="flex"
                                                                                         justifyContent="space-between"
                                                                                         alignItems="center">
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">Status
                                                                                        :</SoftTypography>
                                                                                    <SoftBox sx={{
                                                                                        px: 2,
                                                                                        py: 0.5,
                                                                                        borderRadius: 1,
                                                                                        backgroundColor: cctv.status === 'ACTIVE' ? 'success.main' : 'error.main',
                                                                                        color: 'white',
                                                                                        fontSize: '0.75rem',
                                                                                        fontWeight: 'bold'
                                                                                    }}>
                                                                                        {cctv.status}
                                                                                    </SoftBox>
                                                                                </SoftBox>
                                                                            </SoftBox>
                                                                        </SoftBox>
                                                                    </Card>
                                                                </Grid>

                                                                {/* Cameras Section */}
                                                                <Grid item xs={12} md={8}>
                                                                    <Card sx={{
                                                                        boxShadow: 2,
                                                                        borderRadius: 2,
                                                                        background: 'rgba(255,255,255,0.95)',
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}>
                                                                        <SoftBox p={3}>
                                                                            <SoftBox display="flex" alignItems="center"
                                                                                     justifyContent="space-between"
                                                                                     mb={3}>
                                                                                <SoftTypography variant="h6"
                                                                                                fontWeight="bold"
                                                                                                color="primary">
                                                                                    Cameras
                                                                                    ({cctv?.cameras?.length || 0})
                                                                                </SoftTypography>
                                                                                <SoftButton
                                                                                    onClick={() => handleAddCamera(cctv)}
                                                                                    variant="contained"
                                                                                    color="primary"
                                                                                    startIcon={<Icon>add</Icon>}
                                                                                    sx={{
                                                                                        borderRadius: 2,
                                                                                        textTransform: 'none',
                                                                                        fontWeight: 'bold'
                                                                                    }}
                                                                                >
                                                                                    Add Camera
                                                                                </SoftButton>
                                                                            </SoftBox>

                                                                            {cctv?.cameras?.length > 0 ? (
                                                                                <Grid container spacing={2}>
                                                                                    {cctv?.cameras?.map((camera, idx) => (
                                                                                        <Grid item xs={12} sm={6}
                                                                                              key={idx}>
                                                                                            <Card sx={{
                                                                                                p: 3,
                                                                                                boxShadow: 2,
                                                                                                borderRadius: 3,
                                                                                                background: `linear-gradient(135deg, ${theme.palette.white.main} 0%, ${theme.palette.grey[100]} 100%)`,
                                                                                                border: `1px solid ${theme.palette.grey[300]}`,
                                                                                                transition: 'all 0.3s ease',
                                                                                                '&:hover': {
                                                                                                    transform: 'translateY(-4px)',
                                                                                                    boxShadow: 4
                                                                                                }
                                                                                            }}>
                                                                                                <SoftBox display="flex"
                                                                                                         alignItems="center"
                                                                                                         justifyContent="space-between"
                                                                                                         mb={3}>
                                                                                                    <SoftBox>
                                                                                                        <SoftTypography
                                                                                                            variant="h6"
                                                                                                            fontWeight="bold"
                                                                                                            color="primary">
                                                                                                            {camera.channel}
                                                                                                        </SoftTypography>
                                                                                                        <SoftTypography
                                                                                                            variant="body2"
                                                                                                            color="text.secondary">
                                                                                                            Camera
                                                                                                            Channel                                                                                                        </SoftTypography>
                                                                                                    </SoftBox>
                                                                                                    <SoftBox
                                                                                                        display="flex"
                                                                                                        gap={1}>
                                                                                                        <IconButton
                                                                                                            onClick={() => handleEditCamera(camera, cctv)}
                                                                                                            size="small"
                                                                                                            sx={{
                                                                                                                backgroundColor: 'primary.main',
                                                                                                                color: 'white',
                                                                                                                '&:hover': {
                                                                                                                    backgroundColor: 'primary.dark'
                                                                                                                }
                                                                                                            }}
                                                                                                        >
                                                                                                            <Icon
                                                                                                                fontSize="small">edit</Icon>
                                                                                                        </IconButton>
                                                                                                        <Tooltip
                                                                                                            title={restrictedZoneCounts[camera.cameraId]
                                                                                                                ? `Restricted areas (${restrictedZoneCounts[camera.cameraId]})`
                                                                                                                : 'Set restricted areas'}>
                                                                                                            <IconButton
                                                                                                                onClick={() => handleEditRestrictedZones(camera)}
                                                                                                                size="small"
                                                                                                                sx={{
                                                                                                                    backgroundColor: restrictedZoneCounts[camera.cameraId]
                                                                                                                        ? 'warning.main' : 'secondary.main',
                                                                                                                    color: 'white',
                                                                                                                    '&:hover': {
                                                                                                                        backgroundColor: restrictedZoneCounts[camera.cameraId]
                                                                                                                            ? 'warning.dark' : 'secondary.dark'
                                                                                                                    }
                                                                                                                }}
                                                                                                            >
                                                                                                                <Icon
                                                                                                                    fontSize="small">highlight_alt</Icon>
                                                                                                            </IconButton>
                                                                                                        </Tooltip>
                                                                                                        <IconButton
                                                                                                            onClick={() => handleDeleteCamera(camera)}
                                                                                                            size="small"
                                                                                                            sx={{
                                                                                                                backgroundColor: 'error.main',
                                                                                                                color: 'white',
                                                                                                                '&:hover': {
                                                                                                                    backgroundColor: 'error.dark'
                                                                                                                }
                                                                                                            }}
                                                                                                        >
                                                                                                            <Icon
                                                                                                                fontSize="small">delete</Icon>
                                                                                                        </IconButton>
                                                                                                    </SoftBox>
                                                                                                </SoftBox>

                                                                                                <SoftBox sx={{
                                                                                                    p: 2.5,
                                                                                                    borderRadius: 2,
                                                                                                    backgroundColor: 'rgba(103, 126, 234, 0.05)',
                                                                                                    border: '1px solid rgba(103, 126, 234, 0.1)'
                                                                                                }}>
                                                                                                    <SoftBox sx={{
                                                                                                        display: 'flex',
                                                                                                        flexDirection: 'column',
                                                                                                        gap: 2
                                                                                                    }}>
                                                                                                        {camera.cameraName && (
                                                                                                            <SoftBox
                                                                                                                display="flex"
                                                                                                                justifyContent="space-between"
                                                                                                                alignItems="center">
                                                                                                                <SoftTypography
                                                                                                                    variant="body2"
                                                                                                                    color="text.secondary"
                                                                                                                    fontWeight="medium">
                                                                                                                    Name:
                                                                                                                </SoftTypography>
                                                                                                                <SoftTypography
                                                                                                                    variant="body2"
                                                                                                                    fontWeight="bold"
                                                                                                                    color="primary">
                                                                                                                    {camera.cameraName}
                                                                                                                </SoftTypography>
                                                                                                            </SoftBox>
                                                                                                        )}
                                                                                                        <SoftBox
                                                                                                            display="flex"
                                                                                                            justifyContent="space-between"
                                                                                                            alignItems="center">
                                                                                                            <SoftTypography
                                                                                                                variant="body2"
                                                                                                                color="text.secondary"
                                                                                                                fontWeight="medium">
                                                                                                                Channel:
                                                                                                            </SoftTypography>
                                                                                                            <SoftTypography
                                                                                                                variant="body2"
                                                                                                                fontWeight="bold"
                                                                                                                color="primary">
                                                                                                                {camera.channel}
                                                                                                            </SoftTypography>
                                                                                                        </SoftBox>
                                                                                                        <SoftBox
                                                                                                            display="flex"
                                                                                                            justifyContent="space-between"
                                                                                                            alignItems="center">
                                                                                                            <SoftTypography
                                                                                                                variant="body2"
                                                                                                                color="text.secondary"
                                                                                                                fontWeight="medium">
                                                                                                                Status:
                                                                                                            </SoftTypography>
                                                                                                            <SoftBox
                                                                                                                sx={{
                                                                                                                    px: 2,
                                                                                                                    py: 0.5,
                                                                                                                    borderRadius: 2,
                                                                                                                    backgroundColor: camera.status === 'ACTIVE' ? 'success.main' : 'error.main',
                                                                                                                    color: 'white',
                                                                                                                    fontSize: '0.75rem',
                                                                                                                    fontWeight: 'bold',
                                                                                                                    textTransform: 'uppercase',
                                                                                                                    letterSpacing: '0.5px'
                                                                                                                }}>
                                                                                                                {camera.status}
                                                                                                            </SoftBox>
                                                                                                        </SoftBox>
                                                                                                    </SoftBox>
                                                                                                </SoftBox>
                                                                                            </Card>
                                                                                        </Grid>
                                                                                    ))}
                                                                                </Grid>
                                                                            ) : (
                                                                                <SoftBox sx={{
                                                                                    p: 4,
                                                                                    textAlign: 'center',
                                                                                    backgroundColor: 'rgba(0,0,0,0.05)',
                                                                                    borderRadius: 2
                                                                                }}>
                                                                                    <Icon sx={{
                                                                                        fontSize: 48,
                                                                                        color: 'text.secondary',
                                                                                        mb: 2
                                                                                    }}>videocam_off</Icon>
                                                                                    <SoftTypography variant="h6"
                                                                                                    color="text.secondary"
                                                                                                    mb={1}>
                                                                                        No Cameras
                                                                                    </SoftTypography>
                                                                                    <SoftTypography variant="body2"
                                                                                                    color="text.secondary">
                                                                                        No cameras have been added to
                                                                                        this
                                                                                        CCTV configuration yet.
                                                                                    </SoftTypography>
                                                                                </SoftBox>
                                                                            )}
                                                                        </SoftBox>
                                                                    </Card>
                                                                </Grid>
                                                            </Grid>
                                                        </SoftBox>
                                                    </Card>
                                                </SoftBox>
                                            ))}
                                        </SoftBox>
                                    </Card>
                                )}
                                {activeTab === 2 && (
                                    <SoftBox>
                                        <SoftBox display="flex" alignItems="center" justifyContent="space-between"
                                                 mb={3}>
                                            <SoftTypography variant="h5" fontWeight="bold" color="primary">
                                                Camera Room Mapping ({cameraDetails?.length || 0})
                                            </SoftTypography>
                                            <SoftButton
                                                onClick={handleAddCameraMapping}
                                                variant="contained"
                                                color="primary"
                                                startIcon={<Icon>add</Icon>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Add Mapping
                                            </SoftButton>
                                        </SoftBox>

                                        {cameraDetails?.length > 0 ? (
                                            <Card sx={{
                                                boxShadow: 2,
                                                borderRadius: 3,
                                                background: 'rgba(255,255,255,0.95)',
                                                backdropFilter: 'blur(10px)'
                                            }}>
                                                <CameraRoomMappingTable
                                                    handleDeleteClick={handleDeleteCameraRoomMapping}
                                                    handleEditClick={handleEditCameraMapping}
                                                    rows={cameraDetails}
                                                />
                                            </Card>
                                        ) : (
                                            <SoftBox sx={{
                                                p: 6,
                                                textAlign: 'center',
                                                backgroundColor: 'rgba(0,0,0,0.05)',
                                                borderRadius: 3
                                            }}>
                                                <Icon sx={{
                                                    fontSize: 64,
                                                    color: 'text.secondary',
                                                    mb: 3
                                                }}>settings_input_component</Icon>
                                                <SoftTypography variant="h5" color="text.secondary" mb={2}>
                                                    No Camera Mappings
                                                </SoftTypography>
                                                <SoftTypography variant="body1" color="text.secondary" mb={3}>
                                                    No camera room mappings have been configured yet.
                                                </SoftTypography>
                                                <SoftButton
                                                    onClick={handleAddCameraMapping}
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<Icon>add</Icon>}
                                                    sx={{
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Add First Mapping
                                                </SoftButton>
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Card>
                </Grid>
            </Grid>
            </SoftBox>

            <AddEditCCTVConfigDialog
                open={addEditCCTVDialogOpen}
                onClose={() => setAddEditCCTVDialogOpen(false)}
                cctvConfig={selectedCCTVConfig}
                isEdit={editCCTVMode}
                onSubmit={handleOnSubmitCCTVConfig}
                branch={branch}
            />

            <AddEditRoomDialog
                open={addEditRoomDialogOpen}
                onClose={() => setAddEditRoomDialogOpen(false)}
                room={selectedRoom}
                isEdit={editRoomMode}
                onSubmit={handleOnSubmitRoomConfig}
                branch={branch}
            />
            <DeleteConfirmDialog
                open={deleteCCTVDialogOpen}
                onClose={onCloseDeleteDialog}
                onDelete={handleOnDeleteCCTVConfig}
                warningMessage={`Are you sure you want to delete ${selectedCCTVConfig?.locationDescription || ""} ${selectedCCTVConfig?.host || ""}? This action cannot be undone.`}
            />
            <DeleteConfirmDialog
                open={deleteRoomDialogOpen}
                onClose={onCloseDeleteDialog}
                onDelete={handleOnDeleteRoom}
                warningMessage={`Are you sure you want to delete ${selectedRoom?.number || ""} ${selectedRoom?.description || ""}? This action cannot be undone.`}
            />
            <DeleteConfirmDialog
                open={deleteCameraDialogOpen}
                onClose={onCloseDeleteDialog}
                onDelete={handleOnDeleteCamera}
                warningMessage={`Are you sure you want to delete ${selectedCamera?.channel || ""} ? This action cannot be undone.`}
            />

            <DeleteConfirmDialog
                open={deleteCameraMappingDialogOpen}
                onClose={onCloseDeleteDialog}
                onDelete={handleOnDeleteRoomCameraMapping}
                warningMessage={`Are you sure you want to delete ${selectedMapping?.camera?.channel || ""} ? This action cannot be undone.`}
            />
            <AddEditCameraDialog
                open={addEditCameraDialogOpen}
                onClose={() => setAddEditCameraDialogOpen(false)}
                cctv={selectedCCTVConfig}
                isEdit={editCameraMode}
                onSubmit={handleOnSubmitCameraConfig}
                camera={selectedCamera}
            />
            <RestrictedZoneDialog
                open={restrictedZoneDialogOpen}
                onClose={() => setRestrictedZoneDialogOpen(false)}
                camera={selectedCamera}
                onSaved={loadBranchDetails}
            />
            <AddEditRoomCameraMappingDialog
                open={addEditCameraRoomMappingDialogOpen}
                onClose={() => setAddEditCameraRoomMappingDialogOpen(false)}
                branch={branchDetail}
                isEdit={editCameraRoomMode}
                onSubmit={handleOnSubmitCameraRoom}
                mapping={selectedMapping}
            />
            <ViewCompanyDialog
                open={viewCompanyDialogOpen}
                onClose={() => {
                    setViewCompanyDialogOpen(false);
                    setCompanyToView(null);
                }}
                company={companyToView}
            />
        </>

    );
};

export default BranchDetails; 